import React from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Facebook, Twitter, Instagram, Linkedin, ArrowRight, ShieldCheck, Clock3, HeartPulse, ChevronUp } from "lucide-react";
import LOGO from "@/assets/NIRMAL-HOSPITALS-LOGO-Final.png";


const EXPLORE = [
  ["/about", "About Us"],
  ["/plans", "NPC Plans"],
  ["/benefits", "Benefits"],
  ["/doctors", "Doctors"],
  // ["/why-us", "Why Choose Us"],
  // ["/testimonials", "Testimonials"],
  ["/faq", "FAQ"],
];

const QUICK = [
  ["/corporate", "Corporate"],
  ["/contact", "Contact"],
  // ["/login", "Member Login"],
  ["/join", "Join NPC"],
  ["/privacy", "Privacy Policy"],
  ["/terms", "Terms of Use"],
];

const SOCIAL = [
  { href: "https://www.facebook.com/nirmalhospitals/", icon: Facebook, label: "Facebook",  brand: "hover:bg-[#1877F2]" },
  { href: "https://twitter.com/nirmalhospital1",     icon: Twitter,  label: "Twitter",   brand: "hover:bg-[#1DA1F2]" },
  { href: "https://www.instagram.com/nirmalhospitals/", icon: Instagram, label: "Instagram", brand: "hover:bg-gradient-to-tr hover:from-[#FD5949] hover:to-[#D6249F]" },
  { href: "https://in.linkedin.com/company/nirmalhospitals/", icon: Linkedin, label: "LinkedIn", brand: "hover:bg-[#0A66C2]" },
];

const TRUST = [
  [ShieldCheck, "NABH-aligned", "Safety-first protocols"],
  [HeartPulse,  "25+ specialists", "Across key disciplines"],
  [Clock3,      "24×7 helpline", "Privilege card support"],
];

export default function Footer() {
  return (
    <footer className="relative mt-24 text-white overflow-hidden isolate" data-testid="site-footer">
      {/* Layered background */}
      <div aria-hidden className="absolute inset-0 -z-10 bg-gradient-to-br from-[#0A1A30] via-[#0F2540] to-[#0A1A30]" />
      <div aria-hidden className="absolute inset-0 -z-10 opacity-[0.07] bg-[radial-gradient(rgba(255,255,255,0.6)_1px,transparent_1px)] bg-[length:22px_22px]" />
      <div aria-hidden className="conic-glow absolute -top-32 -left-24 w-[420px] h-[420px] -z-10 opacity-60" />
      <div aria-hidden className="conic-glow absolute -bottom-40 -right-20 w-[360px] h-[360px] -z-10 opacity-40" style={{ animationDirection: "reverse" }} />

      {/* Trust strip */}
      <div className="border-b border-white/10">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-7 grid grid-cols-1 sm:grid-cols-3 gap-4">
          {TRUST.map(([Icon, t, d]) => (
            <div key={t} className="flex items-center gap-3 group">
              <div className="h-11 w-11 rounded-xl grid place-items-center bg-white/8 group-hover:bg-gradient-to-br group-hover:from-[#A53A2A] group-hover:to-[#C89B3C] transition-all duration-500">
                <Icon className="h-5 w-5 text-white" />
              </div>
              <div>
                <div className="font-display text-[15px] font-semibold tracking-tight">{t}</div>
                <div className="text-[12px] text-white/65">{d}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Main grid */}
      <div className="mx-auto max-w-7xl px-6 lg:px-10 py-14 grid grid-cols-1 md:grid-cols-12 gap-12">
        {/* Brand column */}
        <div className="md:col-span-4">
          <div className="bg-white inline-block rounded-md px-3 py-2 shadow-[0_10px_30px_-12px_rgba(165,58,42,0.45)]">
            <img src={LOGO} alt="Nirmal Hospital" className="h-10 w-auto" draggable="false" />
          </div>
          <p className="mt-3 text-[#F5C6BE] font-semibold text-sm italic tracking-wide">Care par excellence</p>
          <p className="mt-4 text-sm text-white/75 leading-relaxed max-w-md">
            A super-specialty health hub in Surat — 25+ doctors, 24×7 emergency and a complete NPC membership program for you and your family.
          </p>

          {/* Socials — brand color on hover */}
          <div className="mt-6 flex items-center gap-2">
            {SOCIAL.map(({ href, icon: Icon, label, brand }) => (
              <a
                key={label}
                href={href}
                target="_blank"
                rel="noreferrer"
                aria-label={label}
                className={`h-10 w-10 grid place-items-center rounded-full bg-white/8 text-white transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg ${brand}`}
              >
                <Icon className="h-4 w-4" />
              </a>
            ))}
          </div>
        </div>

        {/* Explore */}
        <div className="md:col-span-2">
          <h4 className="font-display text-[15px] font-semibold tracking-tight text-white">Explore</h4>
          <span className="block mt-1.5 h-[2px] w-8 rounded-full bg-gradient-to-r from-[#A53A2A] to-[#C89B3C]" />
          <ul className="mt-5 space-y-2.5 text-sm">
            {EXPLORE.map(([to, l]) => (
              <li key={to}>
                <Link to={to} className="text-white/75 hover:text-white transition-colors inline-flex items-center gap-1.5 group">
                  <span className="h-1 w-1 rounded-full bg-[#A53A2A] opacity-0 -ml-2 group-hover:opacity-100 group-hover:ml-0 transition-all" />
                  {l}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        {/* Quick links */}
        <div className="md:col-span-2">
          <h4 className="font-display text-[15px] font-semibold tracking-tight text-white">Quick links</h4>
          <span className="block mt-1.5 h-[2px] w-8 rounded-full bg-gradient-to-r from-[#1E4F87] to-[#C89B3C]" />
          <ul className="mt-5 space-y-2.5 text-sm">
            {QUICK.map(([to, l]) => (
              <li key={to}>
                <Link to={to} className="text-white/75 hover:text-white transition-colors inline-flex items-center gap-1.5 group">
                  <span className="h-1 w-1 rounded-full bg-[#C89B3C] opacity-0 -ml-2 group-hover:opacity-100 group-hover:ml-0 transition-all" />
                  {l}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        {/* Reach */}
        <div className="md:col-span-4">
          <h4 className="font-display text-[15px] font-semibold tracking-tight text-white">Reach us</h4>
          <span className="block mt-1.5 h-[2px] w-8 rounded-full bg-gradient-to-r from-[#A53A2A] via-[#C89B3C] to-[#1E4F87]" />

          <ul className="mt-5 space-y-3.5 text-sm">
            <li className="flex items-start gap-3">
              <div className="h-9 w-9 rounded-lg grid place-items-center bg-white/8 flex-shrink-0"><Phone className="h-4 w-4 text-[#F5C6BE]" /></div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-[#F5C6BE] font-semibold">Privilege Card Helpline</div>
                <a href="tel:+916357033301" className="text-white hover:text-[#C89B3C] transition-colors font-semibold">+91-63570-33301</a>
                <div className="text-[11px] text-white/55">24×7 · Mon to Sun</div>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <div className="h-9 w-9 rounded-lg grid place-items-center bg-white/8 flex-shrink-0"><Mail className="h-4 w-4 text-[#F5C6BE]" /></div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-[#F5C6BE] font-semibold">Email</div>
                <a href="mailto:npc@nirmalhospitals.in" className="text-white hover:text-[#C89B3C] transition-colors">npc@nirmalhospitals.in</a>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <div className="h-9 w-9 rounded-lg grid place-items-center bg-white/8 flex-shrink-0"><MapPin className="h-4 w-4 text-[#F5C6BE]" /></div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-[#F5C6BE] font-semibold">Visit</div>
                <p className="text-white/85 leading-relaxed">Nirmal Super Speciality Hospital<br />Subhash Chandra Bose Marg, Beside Hari Om Petrol Pump, Adajan, L. P. Savani, Surat — 395009, Gujarat</p>
              </div>
            </li>
          </ul>

          <Link to="/join" className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-[14px] font-semibold text-white bg-gradient-to-r from-[#A53A2A] to-[#C89B3C] hover:-translate-y-0.5 transition-all shadow-[0_12px_30px_rgba(165,58,42,0.35)]">
            Join NPC today <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-white/10">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-5 text-xs text-white/60 flex flex-wrap justify-between items-center gap-3">
          <span>© {new Date().getFullYear()} Nirmal Hospital Pvt. Ltd. All rights reserved.</span>
          <span className="flex items-center gap-4">
            <a href="https://koderclub.com" target="_blank" rel="noreferrer" className="text-white/70 hover:text-white transition-colors" data-testid="footer-koderclub-credit">
              Powered by <span className="font-semibold text-[#F5C6BE]">KoderClub</span>
            </a>
            <span className="hidden sm:inline opacity-30">·</span>
            <Link to="/privacy" className="hover:text-white">Privacy</Link>
            <Link to="/terms" className="hover:text-white">Terms</Link>
            <button
              type="button"
              onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
              className="ml-1 h-8 w-8 grid place-items-center rounded-full bg-white/8 hover:bg-white/15 transition-colors"
              aria-label="Back to top"
              data-testid="footer-back-to-top"
            >
              <ChevronUp className="h-4 w-4" />
            </button>
          </span>
        </div>
      </div>
    </footer>
  );
}
